import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { fetchDecisions } from "../lib/workbookApi";
import { formatError } from "../lib/api";
import {
  Gavel, CalendarClock, ChevronRight, ScrollText, AlertTriangle, Loader2, BookOpen,
} from "lucide-react";

// The Decisions log is the single most important screen in the workbook —
// it's the founder's own record of what they committed to, in course order.
// Design intent: this page should FEEL like a formal ledger, not a to-do list.

function DecisionCard({ d }) {
  return (
    <article
      className="rounded-xl border border-border bg-white p-5 sm:p-6 relative overflow-hidden"
      data-testid={`decision-${d.worksheet_id}`}
    >
      {d.review_overdue && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-pink-400/50 text-pink-200 bg-pink-500/10">
          <AlertTriangle className="w-3 h-3" /> Review overdue
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-[color:var(--brand-accent,#E91E63)]/15 grid place-items-center">
          <Gavel className="w-3.5 h-3.5 text-[color:var(--brand-accent,#E91E63)]" />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {d.is_bonus ? "Bonus" : `Module ${d.module_number}`} · Worksheet {d.worksheet_number}
        </div>
      </div>

      <h3 className="font-display text-lg leading-snug">
        {d.worksheet_title}
      </h3>
      {d.question && (
        <p className="mt-1 text-sm text-muted-foreground italic">
          {d.question}
        </p>
      )}

      <div className="mt-4 rounded-lg border border-[color:var(--brand-accent,#E91E63)]/30 bg-[color:var(--brand-accent,#E91E63)]/[0.06] px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand-accent,#E91E63)] font-semibold mb-1">
          Decision
        </div>
        <div className="text-sm font-medium text-foreground">
          {d.selected_option}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85 mb-1">Commitment</div>
        <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
          {d.commitment}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
        {d.review_date && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="w-3.5 h-3.5" />
            Review by {d.review_date}
          </span>
        )}
        {d.submitted_at && (
          <span>Submitted {d.submitted_at.slice(0, 10)}</span>
        )}
        <Link
          to={`/workbook/${d.worksheet_id}`}
          className="ml-auto inline-flex items-center gap-1 text-foreground/70 hover:text-foreground"
          data-testid={`decision-open-${d.worksheet_id}`}
        >
          Open worksheet <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </article>
  );
}

export default function WorkbookDecisions() {
  const [decisions, setDecisions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDecisions()
      .then((d) => setDecisions(d.decisions || []))
      .catch((e) => setError(formatError(e)));
  }, []);

  const grouped = useMemo(() => {
    if (!decisions) return [];
    const map = new Map();
    for (const d of decisions) {
      const key = d.module_id || "misc";
      if (!map.has(key)) {
        map.set(key, {
          module_id: key,
          module_number: d.module_number,
          module_title: d.module_title,
          is_bonus: d.is_bonus,
          items: [],
        });
      }
      map.get(key).items.push(d);
    }
    return Array.from(map.values());
  }, [decisions]);

  const overdueCount = (decisions || []).filter((d) => d.review_overdue).length;

  return (
    <AppShell title="Your Decisions">
      <div className="w-full pb-24">
        <PageHero
          eyebrow="The output of the program"
          icon={Gavel}
          title={<>Your <span className="text-grad">Decisions</span></>}
          subtitle="Every commitment you've made through the course workbook, in the order you made them. Future-you will thank present-you for keeping this honest."
        />

        {overdueCount > 0 && (
          <div
            className="mb-8 rounded-lg border border-pink-400/40 bg-pink-500/10 px-4 py-3 text-sm text-pink-800 flex items-center gap-2"
            data-testid="overdue-banner"
          >
            <AlertTriangle className="w-4 h-4" />
            {overdueCount} decision{overdueCount === 1 ? "" : "s"} passed its review date.
          </div>
        )}

        {error && <div className="surface p-6 text-red-300">{error}</div>}
        {!decisions && !error && (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground/70" />
          </div>
        )}

        {decisions && decisions.length === 0 && (
          <div
            className="rounded-xl border border-border bg-white p-10 text-center"
            data-testid="decisions-empty-card"
          >
            <BookOpen className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-sm text-foreground/70">
              You haven't closed any decision gates yet.
            </p>
            <Link
              to="/workbook"
              className="mt-4 inline-block btn-primary"
              data-testid="decisions-empty-cta"
            >
              Open the workbook
            </Link>
          </div>
        )}

        <div className="space-y-12">
          {grouped.map((g) => (
            <section key={g.module_id}>
              <div className="mb-4 flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {g.is_bonus ? "Bonus" : `Module ${g.module_number}`}
                </div>
                <div className="h-px flex-1 bg-white/10" />
                <div className="text-xs text-muted-foreground">{g.module_title}</div>
              </div>
              <div className="space-y-4">
                {g.items.map((d) => (
                  <DecisionCard key={d.worksheet_id} d={d} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
